import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { arcadeApi, arcadeAuth, RankingEntry, ArcadeProfile } from '../../services/arcade';

const SITE_NAME = import.meta.env.VITE_SITE_NAME || 'Cripto News';
const SITE_URL = import.meta.env.VITE_SITE_URL || 'https://criptonews.site';

const GAMES = [
  {
    to: '/arcade/quiz-diario',
    icon: '🧠',
    title: 'Quiz Diário',
    desc: '5 perguntas sobre notícias e conceitos cripto. Até 50 NC por dia.',
  },
  {
    to: '/arcade/crypto-word',
    icon: '📝',
    title: 'Crypto Word',
    desc: 'Descubra a palavra cripto do dia em até 6 tentativas. Até 30 NC.',
  },
  {
    to: '/arcade/candle-challenge',
    icon: '📉',
    title: 'Candle Challenge',
    desc: 'Analise um gráfico histórico encerrado e identifique o padrão. 20 NC.',
  },
];

export const ArcadePage: React.FC = () => {
  const [ranking, setRanking] = useState<RankingEntry[]>([]);
  const [profile, setProfile] = useState<ArcadeProfile | null>(null);

  useEffect(() => {
    arcadeApi.ranking().then(setRanking).catch(() => {});
    if (arcadeAuth.isLoggedIn()) {
      arcadeApi.me().then(setProfile).catch(() => {});
    }
  }, []);

  return (
    <>
      <Helmet>
        <title>Crypto Arcade: Jogos Educativos de Cripto | {SITE_NAME}</title>
        <meta
          name="description"
          content="Jogos gratuitos e educativos sobre criptomoedas: quiz diário, Crypto Word e Candle Challenge. Ganhe NewsCoins (pontos recreativos sem valor monetário) e suba no ranking."
        />
        <link rel="canonical" href={`${SITE_URL}/arcade`} />
      </Helmet>

      <div className="min-h-screen bg-gray-50">
        <div className="container mx-auto px-4 py-10 max-w-5xl">
          <nav className="text-sm text-gray-500 mb-4 flex items-center gap-2">
            <Link to="/" className="hover:text-blue-600 transition">Início</Link>
            <span>›</span>
            <span className="text-gray-700">Crypto Arcade</span>
          </nav>

          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-3">
            <h1 className="text-3xl md:text-4xl font-bold text-gray-900">🕹️ Crypto Arcade</h1>
            {profile ? (
              <Link
                to="/arcade/perfil"
                className="bg-white rounded-xl shadow px-4 py-2.5 flex items-center gap-3 hover:shadow-lg transition"
              >
                <span className="font-semibold text-gray-900 text-sm">{profile.nickname}</span>
                <span className="bg-yellow-100 text-yellow-800 px-2.5 py-0.5 rounded-full text-xs font-bold">
                  {profile.balance} NC
                </span>
                {profile.streak.current > 0 && (
                  <span className="text-orange-600 text-xs font-bold">🔥 {profile.streak.current}d</span>
                )}
              </Link>
            ) : (
              <span className="text-sm text-gray-500">Crie um perfil ao abrir qualquer jogo</span>
            )}
          </div>
          <p className="text-gray-600 leading-relaxed mb-8 max-w-3xl">
            Jogos rápidos, gratuitos e educativos para aprender cripto se divertindo. Você ganha{' '}
            <strong>NewsCoins (NC)</strong> — pontos recreativos internos, <strong>sem valor monetário</strong>:
            não dá para comprar, vender, sacar nem transferir. Sem apostas, sem prêmios em dinheiro.
          </p>

          {/* Jogos */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-5 mb-10">
            {GAMES.map((game) => (
              <Link
                key={game.to}
                to={game.to}
                className="bg-white rounded-xl shadow p-6 hover:shadow-lg transition"
              >
                <span className="text-4xl block mb-3">{game.icon}</span>
                <h2 className="font-bold text-gray-900 mb-1">{game.title}</h2>
                <p className="text-gray-600 text-sm leading-relaxed">{game.desc}</p>
              </Link>
            ))}
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Ranking */}
            <section className="bg-white rounded-xl shadow p-6">
              <h2 className="text-lg font-bold text-gray-900 mb-4">🏆 Ranking</h2>
              {ranking.length === 0 ? (
                <p className="text-gray-500 text-sm">Seja o primeiro a pontuar!</p>
              ) : (
                <ol className="space-y-2">
                  {ranking.slice(0, 10).map((entry) => (
                    <li key={entry.position} className="flex items-center justify-between text-sm">
                      <span className="flex items-center gap-2">
                        <span className={`w-6 text-center font-bold ${entry.position <= 3 ? 'text-yellow-600' : 'text-gray-400'}`}>
                          {entry.position <= 3 ? ['🥇', '🥈', '🥉'][entry.position - 1] : entry.position}
                        </span>
                        <span className="font-medium text-gray-800">{entry.nickname}</span>
                      </span>
                      <span className="font-bold text-gray-600">{entry.balance} NC</span>
                    </li>
                  ))}
                </ol>
              )}
              <p className="text-xs text-gray-400 mt-4">
                Ranking recreativo — NewsCoins não têm valor monetário.
              </p>
            </section>

            {/* Como funciona */}
            <section className="bg-white rounded-xl shadow p-6">
              <h2 className="text-lg font-bold text-gray-900 mb-4">Como funciona</h2>
              <ul className="space-y-2 text-sm text-gray-700 leading-relaxed list-disc pl-5">
                <li>Cada jogo pode ser jogado <strong>uma vez por dia</strong>.</li>
                <li>Acertos rendem NewsCoins; jogar dias seguidos rende bônus de sequência (🔥 +5 NC/dia).</li>
                <li>Conquiste badges por marcos: quiz perfeito, sequência de 7 e 30 dias, e mais.</li>
                <li>O perfil pede apenas um apelido — sem e-mail, senha ou dados pessoais.</li>
                <li>Novo conteúdo todos os dias de manhã (horário de Brasília).</li>
              </ul>
              <p className="text-xs text-gray-400 mt-4 leading-relaxed">
                O Crypto Arcade é 100% recreativo e educativo: não envolve apostas, compra de chances, prêmios
                financeiros ou saque. As NewsCoins não são uma criptomoeda e não têm qualquer valor fora do site.
              </p>
            </section>
          </div>
        </div>
      </div>
    </>
  );
};
